import type { FormElement, UrlInputProperties } from '../../model/form.types';
import type { FieldDefinition } from '../../model/types';
import { builderDefaultText, singleColumn, stringShapeIssue, validateFormattedText, valueAsText } from './shared';

type UrlInputElement = FormElement & { properties: UrlInputProperties };

export const INVALID_URL_MESSAGE = 'URL inválida.';

const ALLOWED_PROTOCOLS = new Set(['http:', 'https:']);

const SCHEME = /^[a-z][a-z0-9+.-]*:/i;

// The scheme is detected on the text as a browser reads an href, with tabs and newlines removed and
// the edges trimmed: `java\tscript:...` is a `javascript:` link, and once prefixed with `https://`
// it would parse as a user name over a real host. The edges also lose the Unicode blanks a browser
// keeps, such as the invisible no-break space a link copied from Word or a PDF ends with.
function hrefText(text: string): string {
  const chars = [...text.replace(/[\t\n\r]/g, '')];
  const isEdge = (char: string | undefined) => char !== undefined && (char.charCodeAt(0) <= 0x20 || /\s/.test(char));
  while (isEdge(chars[0])) chars.shift();
  while (isEdge(chars[chars.length - 1])) chars.pop();
  return chars.join('');
}

// `https://trusted.com@evil.com` reads as a link to trusted.com but opens evil.com.
function hasNoCredentials(url: URL): boolean {
  return url.username === '' && url.password === '';
}

const DOMAIN_LABEL = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/i;

const TOP_LEVEL_LABEL = /^[a-z]{2,}$/i;

function hasDomainWithExtension(url: URL): boolean {
  const labels = url.hostname.split('.');
  return (
    labels.length >= 2 &&
    labels.every((label) => DOMAIN_LABEL.test(label)) &&
    TOP_LEVEL_LABEL.test(labels[labels.length - 1])
  );
}

// The answer is shown to other people as a link, so a `javascript:` or `data:` URL stored by one
// person would run in the session of whoever clicks it; only http and https are a valid answer.
// The builder accepts a URL without scheme and reads it as https, so that is checked the same way.
// The builder default value uses this same rule, so a default the builder accepts is always a valid answer.
export function isValidUrlText(text: string): boolean {
  const href = hrefText(text);
  // `new URL` turns a space in the path or the query into `%20`, so it has to be refused before it.
  if (/\s/.test(href)) return false;
  try {
    const url = new URL(SCHEME.test(href) ? href : `https://${href}`);
    return ALLOWED_PROTOCOLS.has(url.protocol) && hasNoCredentials(url) && hasDomainWithExtension(url);
  } catch {
    return false;
  }
}

// The value is stored as a full link so whoever opens it later lands on the page that was meant,
// not on a path relative to the system. The text is kept as typed apart from the scheme and what
// `hrefText` drops, because `new URL().href` would also add a trailing slash and lowercase or
// punycode the host.
export function toFullUrl(text: string): string {
  if (!isValidUrlText(text)) return text;
  const href = hrefText(text);
  return SCHEME.test(href) ? href : `https://${href}`;
}

export const urlInputDefinition: FieldDefinition<UrlInputElement> = {
  kind: 'field',
  type: 'URL_INPUT',
  emptyValue: (element) => toFullUrl(builderDefaultText(element)),
  control: (element) => ({ kind: 'text', placeholder: element.properties.placeholder, inputType: 'url' }),
  shapeIssue: stringShapeIssue,
  validate: (element, value) => validateFormattedText(element, value, isValidUrlText, INVALID_URL_MESSAGE),
  columns: singleColumn,
  cells: (_element, value) => [valueAsText(value)],
  triggerMatches: () => false,
};
